import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, Grid, Modal, SvgIcon, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import loader from 'src/components/LoaderImg/loader.gif';
import { goalBankTreatment } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { paths } from 'src/paths';
import {
  checkIsReformAction,
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import { checkSupervisorPermission } from 'src/utils/checkSignatureAccess';
import isEvalFormType from 'src/utils/isEvalFormType';
import DocumentTemplateUnavailable from './document-template-unavailable';
import ReForm from '../reform';
import AddendumNote from '../AddendumNote';
import SucessModal from '../sucess-modal';
import CopyGoalWidget from './copy-goal-widget';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import moment from 'moment';
import DSNSummaryModal from '../dailySessionNotes/DSN summary/DSNSummaryModal';
import WarningModal from '../therapyPlanning_2.0/commonComponent/warning-modal';

const loaderCSS: CSSProperties = {
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  userSelect: 'none',
  zIndex: 9999,
};

function EditDocumentPreview() {
  // const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patients, patientDetails, episodeOfCare, details } = paths;
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const { document, episodeOfCareId, patientData, activeTab } = useSelector(
    (state: any) => state.eocEditDocumentDataReducer
  );
  const [providerId] = useCurrentUser(['providerId']);
  const getSignatureAccess = useSelector((state: any) => state.getSignatureAccessReducer);
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const [checkGoalBankToggle] = useFeatureToggle(goalBankTreatment, 'FACILITY');
  const [initialized, setInitialized] = useState(false);
  const [showPayerWarningModal, setShowPayerWarningModal] = useState(false);
  const [payerErrorEpisodeOfCareId, setPayerErrorEpisodeOfCareId] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (!initialized) {
      dispatch(resetUuidAction());
      setInitialized(true);
    }
  }, [initialized, dispatch]);

  const useReformAddendumNote = document?.formType === 'ADDENDUM_NOTE_REFORM';

  useEffect(() => {
    if (!document) return;
    dispatch(checkIsReformAction(document?.formType?.includes('REFORM')));
  }, [document]);

  const goBackToEOC = () => {
    dispatch(resetUuidAction());
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: episodeOfCareId,
        getPatientId,
      },
    });
  };

  useEffect(() => {
    if (!document) return;
    let fetchMostRecent = false;

    if (document?.reformId) {
      if (document.type === 'PT' || document.type === 'OT' || document.type === 'SLP') {
        fetchMostRecent = true;
      }
    }

    dispatch(
      pullUuidAction(
        {
          uuid: document?.uuid,
          providerId,
          formId: document?.formId,
          episodeOfCareId: episodeOfCareId,
          type: activeTab,
          isForSupervisor: checkSupervisorPermission(
            getSignatureAccess.response?.data?.t1,
            document?.formType
          ),
          entityType: 'EOC',
          documentType: document?.formType,
          //  BRK-2073: the on-behalf discriminator, stashed on the document by the reason modal
          //  in the pending list. Absent on every legacy edit — the backend's mint enforcement
          //  and webhook stamping activate only when this field is present (BRK-2072).
          //  providerId above deliberately stays the CALLER: edit webhooks are actor-keyed and
          //  authorship is preserved server-side.
          ...(document?.onBehalfReason ? { onBehalfReason: document.onBehalfReason } : {}),
        },
        {
          fetchMostRecent,
        }
      )
    );
  }, [document]);

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const closeErrorModal = () => {
    setOpenErrorModal(false);
    dispatch(resetForLoaderDocumentation());
  };

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (isDocLoader.error) {
      setOpenErrorModal(true);
      return;
    }
    if (!isDocLoader.loader) {
      dispatch(resetForLoaderDocumentation());
      goBackToEOC();
    }
  }, [isDocLoader]);

  const getCleanUuid = () => {
    return pullUuidResponse?.response?.trim() || '';
  };

  const isReformsEnabled = () => {
    if (!pullUuidResponse?.response || !document.reformId) {
      return false;
    }

    // Reform addendum notes carry a reformId and a PT/OT/SLP discipline (document.type), so they
    // would otherwise match the discipline check below and render the generic <ReForm>. They have
    // their own <AddendumNote> component (see the useReformAddendumNote branch), so opt out here.
    if (document?.formType === 'ADDENDUM_NOTE_REFORM') {
      return false;
    }

    if (document.type === 'PT' || document.type === 'OT' || document.type === 'SLP') {
      return true;
    }

    return false;
  };

  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);
  const [openSummary, setOpenSummary] = useState(false);

  const handleOpenDsnSummary = () => {
    dispatch(resetDsnSummary());
    dispatch(
      getDSNNoteSummary({
        patientId: getPatientId,
        type: 'progress-note',
        params: {
          discipline: activeTab,
          localDate: document?.dateOfService
            ? document?.dateOfService
            : moment(new Date()).format('YYYY-MM-DD'),
        },
      })
    );
    setOpenSummary(true);
  };

  const handlePayerError = (error: {
    error?: string;
    errorCode?: string;
    errorData?: any;
    episodeOfCareId?: string;
  }) => {
    if (error.errorCode === 'NO_PAYER_ON_CERTIFICATION_START_DATE') {
      setPayerErrorEpisodeOfCareId(error.episodeOfCareId);
      setShowPayerWarningModal(true);
    }
  };

  const handleAssignPayer = () => {
    setShowPayerWarningModal(false);
    navigate(`${patients}/${patientDetails}${paths.payers}`, {
      state: {
        patientId: getPatientId,
      },
    });
  };

  const handleCancelPayerModal = () => {
    setShowPayerWarningModal(false);
    // If episodeOfCareId exists, redirect back to episode of care
    // Otherwise, redirect to therapist dashboard
    if (payerErrorEpisodeOfCareId) {
      goBackToEOC();
    } else {
      navigate(paths.dashboard);
    }
  };

  return (
    <>
      {isDocLoader.loader && (
        <Grid sx={loaderCSS}>
          <img
            src={loader}
            alt="loader.."
          />
        </Grid>
      )}

      <Grid
        container
        justifyContent={'space-between'}
      >
        <Stack
          gap="1.5rem"
          sx={{ padding: '0 4rem', width: '100%' }}
        >
          <Button
            sx={{
              justifyContent: 'flex-start',
              color: '#111927',
              width: 'max-content',
              padding: '0px',
            }}
            onClick={goBackToEOC}
            startIcon={
              <SvgIcon>
                <ArrowBackIcon sx={{ color: '#6C737F' }} />
              </SvgIcon>
            }
          >
            <Typography variant="subtitle1">
              Back to Episode Of Care Details -
              {` ${patientData?.lastName ?? ''} ${patientData?.firstName ?? ''}`}
            </Typography>
          </Button>
          <Stack
            flexDirection="row"
            gap="1rem"
            justifyContent={'space-between'}
            alignItems="center"
          >
            <Stack>
              <Typography variant="h5">Edit Document - {document?.documentType}</Typography>
            </Stack>
            <Stack>
              {document.formType?.includes('WEEKLY_PROGRESS_NOTE') && (
                <Button
                  color="primary"
                  size="small"
                  onClick={() => handleOpenDsnSummary()}
                  variant="outlined"
                >
                  DSN Summary
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
        {checkGoalBankToggle && <CopyGoalWidget />}
      </Grid>

      {pullUuidResponse.response && (
        <Box
          sx={{ height: '100%' }}
          p={3}
        >
          {isReformsEnabled() ? (
            <Box>
              {initialized && (
                <ReForm
                  uuid={getCleanUuid()}
                  formId={document.reformId}
                  onSubmitError={handlePayerError}
                />
              )}
            </Box>
          ) : useReformAddendumNote ? (
            <AddendumNote
              formId={document?.reformId}
              webhookUuid={getCleanUuid()}
              patientData={patientData}
              loadPrefill={Boolean(document?.submissionId)}
            />
          ) : isEvalFormType(document?.formType) ? (
            // BRK-1921: evaluations are reform-only. Without a reformId this used to render the
            // legacy JotForm prefill iframe, silently sending the user back to JotForm.
            <DocumentTemplateUnavailable
              onBack={goBackToEOC}
              backLabel="Back to Episode of Care"
            />
          ) : (
            // Jotform is gone: a legacy Jotform document (it carries a prefillUrl) can no longer
            // be edited, and a Reform type missing its reformId is a template misconfiguration.
            <DocumentTemplateUnavailable
              onBack={goBackToEOC}
              backLabel="Back to Episode of Care"
              reason={document?.prefillUrl ? 'legacy' : 'unconfigured'}
            />
          )}
        </Box>
      )}

      <Modal open={openErrorModal}>
        <SucessModal
          close={closeErrorModal}
          isSucess={false}
          title="Edit a Document"
          message={isDocLoader?.response}
        />
      </Modal>
      <DSNSummaryModal
        open={openSummary}
        title={`DSN Summary | ${activeTab}`}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />
      <WarningModal
        open={showPayerWarningModal}
        onClose={handleCancelPayerModal}
        title="Payer Required"
        message="A certification must be associated with an active payer. Please assign a payer for the selected date before continuing."
        buttonText="Assign Payer"
        cancelBtn="Cancel"
        handleSaveBtn={handleAssignPayer}
      />
    </>
  );
}

export default EditDocumentPreview;
